import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys, onboardingKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import ExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import sentinel_one_step_1 from "./images/sentinel_one_step_1.png";
import sentinel_one_step_2 from "./images/sentinel_one_step_2.png";

const SentinelOneIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const formInitialValues = {
    tenant: "",
    api_token: "",
  };
  const formCheckoutSchema = yup.object().shape({
    tenant: yup.string().required("required"),
    api_token: yup.string().required("required"),
  });
  const handleFormSubmit = (values) => {
    onSubmit(values);
  };

  const sanitizeTenantInput = (tenant) => {
    try {
      const url = new URL(tenant);
      const match = url.hostname.match(/^(.*?)\.sentinelone\.net$/);
      return match ? match[1] : tenant;
    } catch (error) {
      return tenant.replace(/^https?:\/\//, "").split(".")[0];
    }
  };

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("sentinel_one");
  const [loading, setLoading] = useState(false);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerSentinelOneIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    const tenantName = sanitizeTenantInput(values.tenant);
    const url = `https://${tenantName}.sentinelone.net/web/api/v2.1`;

    registerSentinelOneIntegration(url, values.api_token)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        // only in EDR integrations, we need to invalidate the onboarding context
        queryClient.invalidateQueries({
          queryKey: onboardingKeys.all,
        });
        Toast.success("SentinelOne integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading
            ? "Once integrated, the Breeze platform will automatically read the configurations from SentinelOne and prioritize the suggested remediations."
            : ""
        }
        image={logos.SentinelOneLogo}
        name={"SentinelOne"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>SentinelOne is integrated!</Typography>
            </Box>
          ) : (
            <div>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Formik
                  onSubmit={handleFormSubmit}
                  initialValues={formInitialValues}
                  validationSchema={formCheckoutSchema}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <GuidelineStepByStep>
                        <GuidelineStep title="Login into your SentinelOne account">
                          <Typography variant={"body2"}>
                            Login into your SentinelOne account and paste the
                            Tenant URL here.
                          </Typography>
                          <Typography variant={"body2"}>
                            Your SentinelOne console domain should look like
                            this:{" "}
                          </Typography>
                          <Typography variant={"body2"}>
                            https://<strong>tenant-name</strong>
                            .sentinelone.net.
                          </Typography>

                          <Grid item>
                            <TextField
                              fullWidth
                              variant="filled"
                              name="tenant"
                              label="Tenant"
                              value={values.tenant}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={!!touched.tenant && !!errors.tenant}
                              helperText={touched.tenant && errors.tenant}
                              disabled={guideMode}
                            />
                          </Grid>
                        </GuidelineStep>

                        <GuidelineStep title="Configure the API key">
                          {values.tenant.trim() ? (
                            <ExternalLink
                              href={`https://${sanitizeTenantInput(
                                values.tenant
                              )}.sentinelone.net/settings/users/service-users`}
                              text="Follow the URL in your browser to go to the SentinelOne "
                              linkText={"API key configuration page"}
                            />
                          ) : (
                            <Typography variant="body2" color="gray">
                              Follow the URL in your browser to go to the
                              SentinelOne API key configuration page.
                            </Typography>
                          )}
                        </GuidelineStep>
                        <GuidelineStep title="Generate the API key">
                          <Typography variant={"body2"}>
                            In the top left corner, click on the{" "}
                            <b> New Service User </b>
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: sentinel_one_step_1,
                                altext: "sentinel_one service user settings",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            Edit values:
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant={"body2"}>
                              Service user name: <strong>BreezeSecurity</strong>
                            </Typography>
                            <CustomCopyButton
                              copyTitle={"name"}
                              copyText={"BreezeSecurity"}
                            />
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant={"body2"}>
                              Description:{" "}
                              <strong>BreezeSecurity API key</strong>
                            </Typography>
                            <CustomCopyButton
                              copyTitle={"description"}
                              copyText={"BreezeSecurity API key"}
                            />
                          </Box>
                          <Typography variant={"body2"}>
                            Expiration date: <strong>2 years</strong>
                          </Typography>
                          <Typography variant={"body2"}>
                            Assign account-level or site-level permissions as
                            needed.
                          </Typography>
                          <Typography variant={"body2"}>
                            Select the Viewer role, check the account you've
                            created, and click Create Service User.
                          </Typography>

                          <ScreenshotAccordion
                            images={[
                              {
                                file: sentinel_one_step_2,
                                altext: "sentinel_one API key generating",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            Copy the API key by clicking the copy icon.
                          </Typography>
                        </GuidelineStep>

                        <GuidelineStep title="Paste the API key here">
                          <Box width={700}>
                            <Box display="grid" mb="20px" gap="10px">
                              <TextField
                                fullWidth
                                variant="filled"
                                type={showSecret ? "text" : "password"}
                                name="api_token"
                                autoComplete="off"
                                label="API Key"
                                value={values.api_token}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={
                                  !!touched.api_token && !!errors.api_token
                                }
                                helperText={
                                  touched.api_token && errors.api_token
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle secret visibility"
                                        onClick={handleClickShowSecret}
                                        onMouseDown={handleMouseDownSecret}
                                      >
                                        {showSecret ? (
                                          <Visibility />
                                        ) : (
                                          <VisibilityOff />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                disabled={guideMode}
                              />
                            </Box>
                            <Box display="flex" justifyContent="center">
                              <PrimaryButton type="submit" disabled={guideMode}>
                                Connect
                              </PrimaryButton>
                            </Box>
                          </Box>
                        </GuidelineStep>
                      </GuidelineStepByStep>
                    </Form>
                  )}
                </Formik>
              </Box>
            </div>
          )
        }
      />
    </Box>
  );
};

export default SentinelOneIntegration;
